/**
 * Desktop nav hover with Empire-style open/close animations.
 * Only runs if Empire's own nav JS did not initialise nav transitions.
 *
 * Fix: when a nav-parent is entered, only siblings at the SAME depth are
 * closed — never ancestors — so opening a nested submenu never hides the
 * parent mega-menu that is already open.
 */
(function () {
  const DESKTOP_MQ        = window.matchMedia('(min-width: 1024px)');
  const OPEN_DELAY_MS     = 120;
  const CLOSE_DELAY_MS    = 200;
  const CLOSE_DURATION_MS = 250;

  /* ------------------------------------------------------------------ */
  /* Helpers                                                              */
  /* ------------------------------------------------------------------ */

  function empireNavIsActive() {
    return Boolean(
      document.querySelector(
        '[data-site-navigation] .navmenu-submenu[data-animation-state="open"]'
      )
    );
  }

  function setOpenHeight(listitem, submenu) {
    let height = 0;
    for (let i = 0; i < submenu.children.length; i++) {
      height += submenu.children[i].offsetHeight;
    }
    listitem.style.setProperty('--menu-open-height', `${height}px`);
  }

  function animateOpen(submenu, listitem, details, summary) {
    if (submenu.getAttribute('data-animation-state') === 'open') return;
    details.setAttribute('open', '');
    setOpenHeight(listitem, submenu);
    submenu.setAttribute('data-animation-state', 'closed');
    submenu.removeAttribute('data-animation');
    requestAnimationFrame(() => {
      submenu.setAttribute('data-animation', 'closed=>open');
      requestAnimationFrame(() => {
        submenu.setAttribute('data-animation-state', 'open');
        if (summary) summary.setAttribute('aria-expanded', 'true');
      });
    });
  }

  function animateClose(submenu, details, summary, listitem) {
    if (!submenu || submenu.getAttribute('data-animation-state') !== 'open') return;
    submenu.setAttribute('data-animation', 'open=>closed');
    submenu.setAttribute('data-animation-state', 'closed');
    window.setTimeout(() => {
      if (submenu.getAttribute('data-animation-state') !== 'closed') return;
      submenu.removeAttribute('data-animation');
      details.removeAttribute('open');
      if (listitem) listitem.classList.remove('alternate-drop');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }, CLOSE_DURATION_MS);
  }

  /* ------------------------------------------------------------------ */
  /* Depth detection                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Return the navmenu depth of a parent <li> by looking at the closest
   * ancestor <ul class="navmenu-depth-N">.
   * Depth 1 = top-level nav bar items.
   */
  function getNavDepth(parentEl) {
    const ul = parentEl.closest('ul[class*="navmenu-depth-"]');
    if (!ul) return 1;
    const match = ul.className.match(/navmenu-depth-(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }

  /* ------------------------------------------------------------------ */
  /* Global registry keyed by depth                                      */
  /* ------------------------------------------------------------------ */

  /* Map<depth, Array<{parent, details, submenu, summary, closeNow}>> */
  const registryByDepth = new Map();

  function registerEntry(depth, entry) {
    if (!registryByDepth.has(depth)) registryByDepth.set(depth, []);
    registryByDepth.get(depth).push(entry);
  }

  /**
   * Close every open submenu at `depth` except `keepParent`.
   * Does NOT touch any other depth — so ancestors stay open.
   */
  function closeSiblingsExcept(depth, keepParent) {
    const peers = registryByDepth.get(depth) || [];
    peers.forEach(({ parent, closeNow }) => {
      if (parent !== keepParent) closeNow();
    });
  }

  /** Close everything at every depth (used when leaving the nav). */
  function closeAll() {
    registryByDepth.forEach((entries) => {
      entries.forEach(({ closeNow }) => closeNow());
    });
  }

  /* ------------------------------------------------------------------ */
  /* Main bind function                                                   */
  /* ------------------------------------------------------------------ */

  function bindFallback() {
    if (!DESKTOP_MQ.matches) return;
    if (empireNavIsActive()) {
      document.body.removeAttribute('data-nav-fallback');
      return;
    }

    document.body.setAttribute('data-nav-fallback', 'true');

    const nav = document.querySelector('[data-site-navigation]');
    if (!nav) return;

    nav.querySelectorAll('[data-navmenu-parent]').forEach((parent) => {
      if (parent.dataset.navFallbackBound === 'true') return;
      parent.dataset.navFallbackBound = 'true';

      const details = parent.querySelector('[data-navmenu-details]');
      const submenu = parent.querySelector(':scope > details > .navmenu-submenu');
      const summary = details && details.querySelector('summary');

      if (!details || !submenu) return;

      const depth = getNavDepth(parent);

      let openTimer  = null;
      let closeTimer = null;

      const closeNow = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        animateClose(submenu, details, summary, parent);
      };

      const open = () => {
        /*
         * Only close siblings at the SAME depth.
         * This preserves any ancestor mega-menu that is already open.
         */
        closeSiblingsExcept(depth, parent);
        window.clearTimeout(closeTimer);
        const delay = submenu.classList.contains('navmenu-depth-3') ? 0 : OPEN_DELAY_MS;
        openTimer = window.setTimeout(() => {
          animateOpen(submenu, parent, details, summary);
        }, delay);
      };

      const close = () => {
        window.clearTimeout(openTimer);
        closeTimer = window.setTimeout(() => {
          animateClose(submenu, details, summary, parent);
        }, CLOSE_DELAY_MS);
      };

      registerEntry(depth, { parent, details, submenu, summary, closeNow });

      parent.addEventListener('mouseenter', open);
      parent.addEventListener('mouseleave', close);
      parent.addEventListener('focusin',    open);
      parent.addEventListener('focusout', (event) => {
        const next = event.relatedTarget;
        if (!next || !parent.contains(next)) close();
      });

      if (summary) {
        summary.addEventListener('click', (event) => {
          if (!DESKTOP_MQ.matches) return;
          event.preventDefault();
          if (
            submenu.getAttribute('data-animation-state') === 'open' &&
            summary.dataset.href
          ) {
            window.location.href = summary.dataset.href;
          } else {
            open();
          }
        });
      }
    });

    /* ---------------------------------------------------------------- */
    /* Close all when mouse leaves the entire nav or clicks outside      */
    /* ---------------------------------------------------------------- */

    if (!nav.dataset.navLeaveBound) {
      nav.dataset.navLeaveBound = 'true';

      nav.addEventListener('mouseleave', () => {
        closeAll();
      });

      document.addEventListener('click', (event) => {
        if (!nav.contains(event.target)) closeAll();
      }, { capture: true });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  function init() {
    registryByDepth.clear();
    bindFallback();
  }

  function scheduleInit() {
    window.setTimeout(init, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
  } else {
    scheduleInit();
  }

  DESKTOP_MQ.addEventListener('change', init);
  document.addEventListener('shopify:section:load', scheduleInit);
})();
